import React, { useState, useEffect, useCallback } from 'react';

import { Badge } from '../ui/badge';

import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useRcdsSouscriptions({ getAuthHeader, userRole }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [souscriptions, setSouscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dispositifFilter, setDispositifFilter] = useState('all');
  const [expiring, setExpiring] = useState([]);
  const [reminderConfig, setReminderConfig] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [triggeringAll, setTriggeringAll] = useState(false);
  const [updatingConfig, setUpdatingConfig] = useState(false);
  const [exportLoading, setExportLoading] = useState(null);
  const [exportDispositif, setExportDispositif] = useState('all');
  const [attestations, setAttestations] = useState([]);
  const [attPage, setAttPage] = useState(1);
  const [attTotalPages, setAttTotalPages] = useState(1);
  const [attTotal, setAttTotal] = useState(0);
  const [attSearch, setAttSearch] = useState('');
  const [attPeriode, setAttPeriode] = useState('all');
  const [gedLoading, setGedLoading] = useState(null);
  const [expandedAttIds, setExpandedAttIds] = useState(new Set());

  const isAdmin = userRole === 'super_admin' || userRole === 'admin';
  const isSuperAdmin = userRole === 'super_admin';
  // Modale Rectification document signe (Super Admin only)
  const [rectifyModal, setRectifyModal] = useState({ open: false, souscription_id: null });

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rcds-dashboard/stats`, { headers: getAuthHeader() });
      setStats(res.data);
    } catch {
    }
  }, [getAuthHeader]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(), limit: '20',
        statut: statusFilter, dispositif: dispositifFilter
      });
      if (searchQuery) params.append('search', searchQuery);
      const res = await axios.get(`${API}/rcds-dashboard/list?${params}`, { headers: getAuthHeader() });
      setSouscriptions(res.data.souscriptions || []);
      setTotalPages(res.data.pages || 1);
      setTotal(res.data.total || 0);
    } catch {
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, dispositifFilter, searchQuery, getAuthHeader]);

  const fetchExpiring = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/rcds-dashboard/expiring?days=30`, { headers: getAuthHeader() });
      setExpiring(res.data.expiring || []);
    } catch {
    }
  }, [getAuthHeader]);

  const fetchConfig = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/rcds-dashboard/reminder-config`, { headers: getAuthHeader() });
      setReminderConfig(res.data.config);
    } catch {
    }
  }, [getAuthHeader, isAdmin]);

  const fetchAttestations = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: attPage.toString(), limit: '20', periode: attPeriode });
      if (attSearch) params.append('search', attSearch);
      const res = await axios.get(`${API}/rcds-dashboard/attestations?${params}`, { headers: getAuthHeader() });
      setAttestations(res.data.attestations || []);
      setAttTotalPages(res.data.pages || 1);
      setAttTotal(res.data.total || 0);
    } catch {
    }
  }, [attPage, attSearch, attPeriode, getAuthHeader]);

  useEffect(() => {
    fetchStats();
    fetchExpiring();
    fetchConfig();
  }, [fetchStats, fetchExpiring, fetchConfig]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { if (activeTab === 'attestations') fetchAttestations(); }, [activeTab, fetchAttestations]);

  const handleDownloadAttestation = async (attestation) => {
    try {
      const sid = attestation.id || attestation.souscription_id;
      const res = await axios.get(`${API}/souscriptions/${sid}/download-pack1?draft=false`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `ATTESTATION_SIGNEE_${attestation.numero_police || sid}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('Attestation signee telechargee');
    } catch {
      toast.error('Erreur telechargement attestation');
    }
  };

  const handleViewAttestation = async (attestation) => {
    try {
      const sid = attestation.id || attestation.souscription_id;
      const res = await axios.get(`${API}/souscriptions/${sid}/download-pack1?draft=false&preview=true`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(blobUrl, '_blank');
    } catch {
      toast.error('Erreur visualisation attestation');
    }
  };

  const handleDownloadCPCG = async (attestation) => {
    try {
      const sid = attestation.id || attestation.souscription_id;
      const res = await axios.get(`${API}/souscriptions/${sid}/download-pack2?draft=false`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = `CP_CG_SIGNES_${attestation.numero_police || sid}.pdf`;
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success('CP/CG signes telecharges');
    } catch {
      toast.error('Erreur telechargement CP/CG');
    }
  };

  const handleClasserGED = async (souscriptionId) => {
    setGedLoading(souscriptionId);
    try {
      const res = await axios.post(`${API}/rcds-dashboard/classer-ged/${souscriptionId}`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || 'Attestation classee dans la GED');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur classement GED');
    } finally { setGedLoading(null); }
  };

  const toggleAttExpand = useCallback((id) => {
    setExpandedAttIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const handleSendReminder = async (id, _email) => {
    setSendingId(id);
    try {
      const res = await axios.post(`${API}/rcds-dashboard/send-reminder/${id}`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message || `Rappel envoye`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur envoi rappel');
    } finally {
      setSendingId(null);
    }
  };

  const handleTriggerAll = async () => {
    setTriggeringAll(true);
    try {
      const res = await axios.post(`${API}/rcds-dashboard/trigger-reminders`, {}, { headers: getAuthHeader() });
      toast.success(res.data.message);
      fetchConfig();
    } catch {
      toast.error('Erreur declenchement rappels');
    } finally {
      setTriggeringAll(false);
    }
  };

  const handleToggle = async () => {
    setUpdatingConfig(true);
    try {
      const newVal = !reminderConfig?.enabled;
      await axios.post(`${API}/rcds-dashboard/reminder-config`, { enabled: newVal }, { headers: getAuthHeader() });
      setReminderConfig(prev => ({ ...prev, enabled: newVal }));
      toast.success(newVal ? 'Rappels actives' : 'Rappels desactives');
    } catch {
      toast.error('Erreur mise a jour');
    } finally {
      setUpdatingConfig(false);
    }
  };

  const handleUpdateIntervals = async (intervals) => {
    setUpdatingConfig(true);
    try {
      await axios.post(`${API}/rcds-dashboard/reminder-config`, { intervals_days: intervals }, { headers: getAuthHeader() });
      setReminderConfig(prev => ({ ...prev, intervals_days: intervals }));
      toast.success('Intervalles mis a jour');
    } catch {
      toast.error('Erreur');
    } finally {
      setUpdatingConfig(false);
    }
  };

  const handleExportCSV = async () => {
    setExportLoading('csv');
    try {
      const res = await axios.post(
        `${API}/rcds-dashboard/export/csv`,
        { statut: statusFilter, dispositif: exportDispositif },
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `souscriptions_rcds_${new Date().toISOString().slice(0,10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export CSV telecharge');
    } catch {
      toast.error('Erreur export CSV');
    } finally {
      setExportLoading(null);
    }
  };

  const handleExportExcel = async () => {
    setExportLoading('excel');
    try {
      const res = await axios.post(
        `${API}/rcds-dashboard/export/excel`,
        { statut: statusFilter, dispositif: exportDispositif },
        { headers: getAuthHeader(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `souscriptions_rcds_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Export Excel telecharge');
    } catch {
      toast.error('Erreur export Excel');
    } finally {
      setExportLoading(null);
    }
  };

  const getStatusBadge = (status) => {
    const map = {
      'active': { label: 'Active', cls: 'bg-green-600 text-white' },
      'en_attente_paiement': { label: 'Att. paiement', cls: 'bg-amber-600 text-white' },
      'en_cours': { label: 'En cours', cls: 'bg-blue-600 text-white' },
      'expiree': { label: 'Expiree', cls: 'bg-red-600 text-white' },
      'expirant': { label: 'Expirant', cls: 'bg-orange-600 text-white' },
      'archivee': { label: 'Archivee', cls: 'bg-gray-600 text-white' },
    };
    const cfg = map[status] || { label: status || 'N/A', cls: 'bg-gray-500 text-white' };
    return <Badge className={cfg.cls}>{cfg.label}</Badge>;
  };

  const getDaysBadge = (days) => {
    if (days == null) return null;
    let cls = 'bg-green-600 text-white';
    if (days <= 1) cls = 'bg-red-600 text-white';
    else if (days <= 7) cls = 'bg-orange-600 text-white';
    else if (days <= 15) cls = 'bg-amber-600 text-white';
    else if (days <= 30) cls = 'bg-blue-600 text-white';
    return <Badge className={`${cls} text-xs font-bold`}>{days <= 0 ? 'Expire' : `J-${days}`}</Badge>;
  };

  return { exportDispositif, setExportDispositif, dispositifFilter, setDispositifFilter, activeTab, attPage, attPeriode, attSearch, attTotal, attTotalPages, attestations, expandedAttIds, expiring, exportLoading, fetchAttestations, fetchConfig, fetchExpiring, fetchList, fetchStats, gedLoading, getAuthHeader, getDaysBadge, getStatusBadge, handleClasserGED, handleDownloadAttestation, handleDownloadCPCG, handleExportCSV, handleExportExcel, handleSendReminder, handleToggle, handleTriggerAll, handleUpdateIntervals, handleViewAttestation, isAdmin, isSuperAdmin, loading, page, rectifyModal, reminderConfig, searchQuery, sendingId, souscriptions, stats, statusFilter, toggleAttExpand, total, totalPages, triggeringAll, updatingConfig, userRole, setAttTotalPages, setAttTotal, setStatusFilter, setSendingId, setAttSearch, setUpdatingConfig, setExpiring, setTriggeringAll, setTotal, setRectifyModal, setGedLoading, setExportLoading, setTotalPages, setReminderConfig, setAttPeriode, setAttestations, setStats, setAttPage, setSouscriptions, setPage, setExpandedAttIds, setLoading, setActiveTab, setSearchQuery };
}
